import { Box, Stack, createStyles, keyframes } from '@mantine/core';
import { CopyButton } from '@repo/foundations';
import { animation } from '@repo/theme/primitives';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { sanitizePromptMessage } from '../../../components/AIAssistant/utils';
import { RichEditor } from '../../../components/RichEditor';
import type { AiChatStore } from '../store';
import type { PrefilledAgentData } from '../types';
import { AskQuestionButton } from './AskQuestionButton';
import { CreateAgentMenuItem } from './CreateAgentMenuItem';
import { MessageToolbar } from './MessageToolbar';
import { PromptAttachments } from './PromptAttachments';

const fadeUp = keyframes({
	'0%': {
		opacity: 0,
		transform: 'translateY(24px)',
	},
	'100%': {
		opacity: 1,
		transform: 'translateY(0)',
	},
});

const fadeUpAnimation = animation(
	fadeUp,
	'duration-300',
	'ease',
	{ opacity: 0, transform: 'translateY(24px)' },
	'forwards'
);

const useStyles = createStyles(
	(
		theme,
		{ isSidebar, canAnimate }: { isSidebar: boolean; canAnimate: boolean }
	) => ({
		userMessageContainer: {
			display: 'flex',
			maxWidth: isSidebar ? '100%' : '90%',
			alignSelf: 'flex-end',
			...(canAnimate && fadeUpAnimation),
		},
		userMessageContent: {
			backgroundColor: theme.other.getColor('fill/secondary/default'),
			borderRadius: `${theme.radius.md} ${theme.radius.md} ${theme.radius.sm} ${theme.radius.md}`,
			padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
			maxWidth: '100%',
		},
		userToolbar: {
			alignSelf: 'flex-end',
		},
	})
);

export const UserMessage = observer(
	({
		id,
		store,
		onCreateAgent,
	}: {
		id: string;
		store: AiChatStore;
		onCreateAgent: (data: PrefilledAgentData) => void;
	}) => {
		const message = store.getMessage(id)!;

		const canAnimate = ['pending', 'running'].includes(
			message.prompt.status ?? 'pending'
		);

		const { classes } = useStyles({
			isSidebar: store.isSidebar,
			canAnimate,
		});

		const handleCreateAgentFromMenu = useCallback(() => {
			onCreateAgent?.({
				title: '',
				prompt: message.prompt.prompt || '',
				cronExpression: '',
			});
		}, [message.prompt.prompt, onCreateAgent]);

		return (
			<MessageToolbar.Wrapper hideOnHover>
				<Stack
					align="flex-end"
					spacing={0}
					className={classes.userMessageContainer}
				>
					<PromptAttachments message={message.prompt} />
					<Box className={classes.userMessageContent}>
						<RichEditor
							readOnly
							value={sanitizePromptMessage(message.prompt.prompt)}
						/>
					</Box>
					<MessageToolbar className={classes.userToolbar}>
						<CopyButton value={message.prompt.prompt || ''} />
						{!!store.initialPrompt && (
							<AskQuestionButton
								onClick={() => {
									store.onCreateQuestion(
										message.prompt,
										store.initialPrompt!,
										'prompt'
									);
								}}
							/>
						)}

						<CreateAgentMenuItem
							key="create-agent"
							onClick={handleCreateAgentFromMenu}
							asButton
						/>
					</MessageToolbar>
				</Stack>
			</MessageToolbar.Wrapper>
		);
	}
);
